import { MdCampaign } from 'react-icons/md'
import { defineField } from 'sanity'

export default defineField({
  title: 'Announcement Banner',
  name: 'announcementBanner',
  type: 'document',
  fieldsets: [{ name: 'display', title: 'Display Rules' }],
  fields: [
    {
      title: 'Visibility',
      name: 'visibility',
      type: 'object',
      description:
        'Specify the conditions that must be met for this banner to be shown',
      options: {
        collapsible: true
      },
      fields: [
        {
          title: 'References CBD',
          name: 'cbd',
          type: 'boolean',
          description:
            'The banner should be excluded from "scrubbed" or CBD-free storefronts',
          initialValue: false,
          options: {
            layout: 'checkbox'
          }
        },
        {
          title: 'Active Shopify discount',
          name: 'discount',
          type: 'boolean',
          description:
            'The banner should be shown only when an auto-applied discount is active',
          initialValue: false,
          options: {
            layout: 'checkbox'
          }
        },
        {
          title: 'Devotion affiliate lead',
          name: 'devotion',
          type: 'boolean',
          description:
            'The banner should be shown only for Devotion-generated sessions',
          initialValue: false,
          options: {
            layout: 'checkbox'
          }
        }
      ]
    },
    {
      title: 'Contents',
      name: 'contents',
      type: 'array',
      of: [
        {
          title: 'Content',
          name: 'content',
          type: 'object',
          fields: [
            {
              title: 'Text',
              name: 'text',
              type: 'string',
              description:
                "Special values may be specified with '{{ braces }}', and these will be replaced at runtime. For example, active discount codes can be referenced with '{{ code }}'.",
              validation: Rule => Rule.required()
            },
            {
              title: 'Link',
              name: 'link',
              type: 'object',
              description:
                "When a link is specified here, the entire text string becomes clickable. To instead make only a section of the text clickable, use the format '{{ link | * }}' in the text above. For example, 'Click {{ link | here }}' becomes 'Click h͟e͟r͟e͟'.",
              fields: [
                {
                  title: 'URL',
                  name: 'url',
                  type: 'url',
                  validation: Rule => Rule.uri({ allowRelative: true })
                },
                {
                  title: 'External',
                  name: 'external',
                  type: 'boolean',
                  description: 'Open in a new tab or window'
                }
              ]
            }
          ]
        }
      ],
      validation: Rule =>
        Rule.custom((value, context) => {
          if (context.document?.static && value?.length > 1) {
            return 'Only one entry may be specified for stationary banners.'
          }
          return true
        })
    },
    {
      title: 'Stationary',
      name: 'static',
      type: 'boolean',
      description: 'Banner content should not repeat or scroll',
      initialValue: false
    },
    {
      title: 'Colors',
      name: 'color',
      type: 'object',
      options: {
        collapsible: true,
        collapsed: true
      },
      fields: [
        {
          title: 'Background color',
          name: 'background',
          type: 'color',
          options: {
            colorList: ['#1d2a58', '#25385b', '#a2b0ff', '#ffffff'],
            disableAlpha: true
          },
          initialValue: {
            alpha: 1,
            hex: '#1d2a58',
            hsl: { a: 1, h: 226.78, l: 0.23, s: 0.5 },
            hsv: { a: 1, h: 226.78, s: 0.67, v: 0.35 },
            rgb: { a: 1, b: 88, g: 42, r: 29 }
          }
        },
        {
          title: 'Text color',
          name: 'text',
          type: 'color',
          options: {
            colorList: ['#1d2a58', '#25385b', '#a2b0ff', '#ffffff'],
            disableAlpha: true
          },
          initialValue: {
            alpha: 1,
            hex: '#ffffff',
            hsl: { a: 1, h: 0, l: 1, s: 0 },
            hsv: { a: 1, h: 0, s: 0, v: 1 },
            rgb: { a: 1, b: 255, g: 255, r: 255 }
          }
        }
      ]
    }
  ],
  preview: {
    select: {
      contents: 'contents',
      visibility: 'visibility'
    },
    prepare({ contents, visibility }) {
      const title =
        contents
          ?.map(o => o.text)
          .filter(Boolean)
          .join(' • ') || 'No content'

      let subtitle

      let rules = Object.entries(visibility || {}).filter(
        ([, active]) => active
      )

      if (rules.length > 0) {
        rules = rules.map(([key]) => {
          if (key === 'cbd') {
            return 'only in CBD-friendly storefronts'
          }
          if (key === 'discount') {
            return 'with active discounts'
          }
          if (key === 'devotion') {
            return 'for Devotion leads'
          }
          return key
        }, '')
      } else {
        rules = ['always']
      }

      subtitle = `VISIBLE: ${rules.join(' • ')}`

      return { title, subtitle }
    }
  },
  icon: MdCampaign
})
